import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

export const Signup = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "candidate" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success("Account created");
      navigate(u.role === "recruiter" ? "/recruiter" : "/candidate/resume");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8">
      <SEO title="Create account" description="Sign up for Axiony — free while in beta. Post jobs or upload a résumé and get instant AI match scores." path="/signup" />
      <form onSubmit={submit} className="w-full max-w-md space-y-5 bg-[#111827] border border-white/10 rounded-xl p-8">
        <div>
          <h1 className="font-display text-3xl font-semibold">Create account</h1>
          <p className="text-sm text-slate-400 mt-2">Get instant AI match scores in under a minute.</p>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">I'm signing up as</Label>
          <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} className="grid grid-cols-2 gap-3">
            <label className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer transition-colors ${form.role === "candidate" ? "border-[#06B6D4] bg-[#06B6D4]/5" : "border-white/10"}`}>
              <RadioGroupItem value="candidate" id="r-candidate" data-testid="signup-role-candidate" />
              <div>
                <div className="text-sm font-medium">Candidate</div>
                <div className="text-xs text-slate-500">Find your fit</div>
              </div>
            </label>
            <label className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer transition-colors ${form.role === "recruiter" ? "border-[#06B6D4] bg-[#06B6D4]/5" : "border-white/10"}`}>
              <RadioGroupItem value="recruiter" id="r-recruiter" data-testid="signup-role-recruiter" />
              <div>
                <div className="text-sm font-medium">Recruiter</div>
                <div className="text-xs text-slate-500">Rank applicants</div>
              </div>
            </label>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name" className="text-slate-300">Full name</Label>
          <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                 data-testid="signup-name-input" className="bg-[#0B0F19] border-white/10" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-300">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                 data-testid="signup-email-input" className="bg-[#0B0F19] border-white/10" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-slate-300">Password (6+ chars)</Label>
          <Input id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                 data-testid="signup-password-input" className="bg-[#0B0F19] border-white/10" />
        </div>

        <Button type="submit" disabled={busy} data-testid="signup-submit-btn" className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg py-6">
          {busy ? "Creating…" : "Create account"}
        </Button>

        <div className="text-sm text-slate-400 text-center">
          Already have one? <Link to="/login" className="text-[#06B6D4] hover:underline">Sign in</Link>
        </div>
      </form>
    </div>
  );
};
